import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, X, KeyRound, Sparkles, Bot, ScrollText, ShieldCheck } from "lucide-react";

const ALL_AMENITIES = ["Free WiFi", "Pool", "Spa", "Restaurant", "Free Parking", "AC", "Gym", "Bar", "Banquet Hall", "Conference Room", "EV Charging", "Rooftop", "Sea View", "Valet", "Business Center", "Pet Friendly", "Airport Shuttle"];
const CATEGORIES = ["3-star", "4-star", "5-star", "boutique", "heritage", "business", "resort"];
const EMPTY_FORM = {
  name: "", city: "", address: "", description: "", base_price: 3000,
  amenities: [], photos: [],
  contact_name: "", contact_phone: "", contact_email: "",
  category: "3-star", map_url: "",
  nearby_attractions: [], near_airport: "", near_railway: "", near_bus_stand: "",
  gst: "", gst_url: "", pan: "", pan_url: "",
  bank_details: "", bank_proof_url: "",
  trade_license_url: "", owner_id_proof_url: "",
  auto_bid_enabled: false, auto_bid_style: "dynamic", auto_bid_min_price: "", auto_bid_max_discount_pct: 25,
  rules_preset: [], rules_custom: [], checkin_time: "13:00", checkout_time: "11:00",
};

export default function PartnerProfile() {
  const [hotel, setHotel] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoUrl, setPhotoUrl] = useState("");
  const [attraction, setAttraction] = useState("");

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [rulePresets, setRulePresets] = useState([]);
  const [customRule, setCustomRule] = useState("");

  useEffect(() => {
    api.get("/my/hotel").then(({ data }) => {
      if (data?.hotel) {
        setHotel(data.hotel);
        setForm({ ...EMPTY_FORM, ...data.hotel });
      }
    }).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/hotel-rule-presets").then(({ data }) => setRulePresets(data.presets || [])).catch((e) => console.debug("fetch failed:", e?.message));
  }, []);

  const toggleRulePreset = (key) => setForm((f) => ({
    ...f,
    rules_preset: (f.rules_preset || []).includes(key)
      ? f.rules_preset.filter((x) => x !== key)
      : [...(f.rules_preset || []), key],
  }));
  const addCustomRule = () => {
    const val = customRule.trim();
    if (!val) return;
    setForm((f) => ({ ...f, rules_custom: [...(f.rules_custom || []), val] }));
    setCustomRule("");
  };
  const removeCustomRule = (idx) => setForm((f) => ({
    ...f,
    rules_custom: (f.rules_custom || []).filter((_, i) => i !== idx),
  }));

  const toggleAmenity = (a) => setForm((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));
  const addPhoto = () => { if (!photoUrl) return; setForm({ ...form, photos: [...form.photos, photoUrl] }); setPhotoUrl(""); };
  const addAttraction = () => { if (!attraction) return; setForm({ ...form, nearby_attractions: [...(form.nearby_attractions || []), attraction] }); setAttraction(""); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (hotel) { await api.put("/my/hotel", form); toast.success("Updated"); }
      else { await api.post("/hotels", form); toast.success("Submitted! Admin will review within 24-48 hrs."); }
      const { data } = await api.get("/my/hotel");
      if (data?.hotel) { setHotel(data.hotel); setForm({ ...EMPTY_FORM, ...data.hotel }); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Passwords don't match");
    if (pwForm.new_password.length < 8) return toast.error("Password must be at least 8 chars");
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Hotel Profile</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">{hotel ? `Editing ${hotel.name}` : "Register your hotel"}</h1>
        {hotel && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className={`hw-chip border ${hotel.status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : hotel.status === "pending" ? "border-emerald-300 bg-emerald-50 text-blue-700" : "border-red-300 bg-red-50 text-red-700"}`}>Status: {hotel.status}</span>
            <span className="hw-chip border-slate-300 bg-slate-50 text-slate-700">Plan: {hotel.plan}</span>
          </div>
        )}

        <Tabs defaultValue="details" className="mt-8">
          <TabsList className="bg-white border border-slate-200 rounded-sm">
            <TabsTrigger value="details" data-testid="tab-details" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Hotel details</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-docs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Documents</TabsTrigger>
            <TabsTrigger value="autobid" data-testid="tab-autobid" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">AI Auto-Bidding</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">House Rules</TabsTrigger>
            <TabsTrigger value="virtual" data-testid="tab-virtual" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Virtual Room</TabsTrigger>
            <TabsTrigger value="pms" data-testid="tab-pms" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">PMS / Channel Manager</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Staff</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Bank & Settlement</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 rounded-sm font-bold">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <form onSubmit={save} className="hw-card p-7 mt-5 space-y-5" data-testid="hotel-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="hw-label">Hotel name *</Label><Input data-testid="hf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" required /></div>
                <div><Label className="hw-label">City *</Label><Input data-testid="hf-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm mt-1" required /></div>
              </div>
              <div><Label className="hw-label">Address</Label><Input data-testid="hf-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1" placeholder="Building, Street, Locality, City, PIN" /></div>
              <div><Label className="hw-label">Description</Label><Textarea data-testid="hf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" rows={3} /></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="hw-label">Base price (₹/night)</Label><Input data-testid="hf-price" type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
                <div>
                  <Label className="hw-label">Hotel category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="hf-category" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <Label className="hw-label">Amenities</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ALL_AMENITIES.map((a) => (
                    <button type="button" key={a} onClick={() => toggleAmenity(a)} className={`hw-chip border ${form.amenities.includes(a) ? "border-blue-600 bg-emerald-50 text-emerald-800 font-semibold" : "border-slate-200 bg-white text-slate-700"}`} data-testid={`amenity-${a.replace(/\s/g, '-')}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <Label className="hw-label">Photos</Label>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="hf-photo-url" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://image-url.jpg" className="rounded-sm" />
                  <Button type="button" onClick={addPhoto} className="rounded-sm bg-slate-900 text-white" data-testid="hf-photo-add"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.photos.map((p, i) => (
                    <div key={p} className="relative">
                      <img src={p} alt="" className="w-20 h-20 object-cover rounded-sm border border-slate-200" />
                      <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 grid place-items-center text-xs rounded-full"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 grid md:grid-cols-3 gap-3">
                <div className="md:col-span-3"><Label className="hw-label">Owner / Contact name</Label><Input data-testid="hf-contact-name" value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="hw-label">Contact phone</Label><Input data-testid="hf-contact-phone" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm mt-1" placeholder="9XXXXXXXXX" /></div>
                <div className="md:col-span-2"><Label className="hw-label">Contact email</Label><Input data-testid="hf-contact-email" type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-sm mt-1" /></div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <Label className="hw-label">Live location (Google Maps URL or lat,lng)</Label>
                <Input data-testid="hf-map-url" value={form.map_url || ""} onChange={(e) => setForm({ ...form, map_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://maps.google.com/?q=..." />
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div><Label className="hw-label">Near airport</Label><Input data-testid="hf-airport" value={form.near_airport || ""} onChange={(e) => setForm({ ...form, near_airport: e.target.value })} className="rounded-sm mt-1" placeholder="8 km from XXX Airport" /></div>
                <div><Label className="hw-label">Near railway</Label><Input data-testid="hf-railway" value={form.near_railway || ""} onChange={(e) => setForm({ ...form, near_railway: e.target.value })} className="rounded-sm mt-1" placeholder="2 km from XXX Station" /></div>
                <div><Label className="hw-label">Near bus stand</Label><Input data-testid="hf-bus" value={form.near_bus_stand || ""} onChange={(e) => setForm({ ...form, near_bus_stand: e.target.value })} className="rounded-sm mt-1" placeholder="1 km from XXX Bus Stand" /></div>
              </div>

              <div>
                <Label className="hw-label">Nearby attractions</Label>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="hf-attraction" value={attraction} onChange={(e) => setAttraction(e.target.value)} placeholder="e.g. Gateway of India (3 km)" className="rounded-sm" />
                  <Button type="button" onClick={addAttraction} className="rounded-sm bg-slate-900 text-white" data-testid="hf-attraction-add"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(form.nearby_attractions || []).map((a, i) => (
                    <span key={a} className="hw-chip border-slate-200 bg-white text-slate-700">
                      {a}
                      <button type="button" onClick={() => setForm({ ...form, nearby_attractions: form.nearby_attractions.filter((_, x) => x !== i) })} className="ml-1 text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>

              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold py-5 h-auto px-7" data-testid="hf-submit">
                {hotel ? "Save changes" : "Submit for approval"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="docs">
            <form onSubmit={save} className="hw-card p-7 mt-5 space-y-5" data-testid="docs-form">
              <p className="text-sm text-slate-600">Provide URLs to your verification documents. Admin will check these before approval.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="hw-label">GST number</Label><Input data-testid="hf-gst" value={form.gst || ""} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="rounded-sm mt-1" placeholder="29ABCDE1234F1Z5" /></div>
                <div><Label className="hw-label">GST certificate URL</Label><Input data-testid="hf-gst-url" value={form.gst_url || ""} onChange={(e) => setForm({ ...form, gst_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://...gst.pdf" /></div>
                <div><Label className="hw-label">PAN number</Label><Input data-testid="hf-pan" value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="rounded-sm mt-1" placeholder="ABCDE1234F" /></div>
                <div><Label className="hw-label">PAN card URL</Label><Input data-testid="hf-pan-url" value={form.pan_url || ""} onChange={(e) => setForm({ ...form, pan_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://...pan.pdf" /></div>
                <div className="md:col-span-2"><Label className="hw-label">Bank details</Label><Input data-testid="hf-bank" value={form.bank_details || ""} onChange={(e) => setForm({ ...form, bank_details: e.target.value })} className="rounded-sm mt-1" placeholder="HDFC • XXXX1234 • IFSC HDFC0001234" /></div>
                <div className="md:col-span-2"><Label className="hw-label">Cancelled cheque / Bank proof URL</Label><Input data-testid="hf-bank-url" value={form.bank_proof_url || ""} onChange={(e) => setForm({ ...form, bank_proof_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://...bank-proof.pdf" /></div>
                <div><Label className="hw-label">Hotel trade license URL</Label><Input data-testid="hf-trade-url" value={form.trade_license_url || ""} onChange={(e) => setForm({ ...form, trade_license_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://...license.pdf" /></div>
                <div><Label className="hw-label">Owner ID proof (Aadhaar/PAN) URL</Label><Input data-testid="hf-owner-id-url" value={form.owner_id_proof_url || ""} onChange={(e) => setForm({ ...form, owner_id_proof_url: e.target.value })} className="rounded-sm mt-1" placeholder="https://...aadhaar.pdf" /></div>
              </div>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold py-5 h-auto px-7" data-testid="docs-save-btn">
                Save documents
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="autobid">
            <div className="hw-card p-7 mt-5 space-y-5" data-testid="autobid-form">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-emerald-500 to-blue-600 grid place-items-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="hw-label text-blue-700">AI Auto-Bidding</div>
                  <h2 className="hw-head text-2xl md:text-3xl font-black text-slate-900 mt-1">Let AI draft bids for you</h2>
                  <p className="text-slate-600 mt-2 text-sm">
                    When travellers post booking requests in your city, our AI reads the request and drafts a competitive bid on your behalf — using your historical accepted prices, current market
                    conditions, competing bids, and demand signals. You always review and hit <b>Send</b> before it reaches the traveller.
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <Label className="hw-head font-bold text-slate-900 text-base">Enable Auto-Bidding for this hotel</Label>
                  <p className="text-xs text-slate-500 mt-1">Default: OFF. Draft mode — nothing is sent without your approval.</p>
                </div>
                <Switch
                  checked={!!form.auto_bid_enabled}
                  onCheckedChange={(v) => setForm({ ...form, auto_bid_enabled: v })}
                  data-testid="autobid-enabled-switch"
                />
              </div>

              <div className="border-t border-slate-100 pt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="hw-label">Bidding style</Label>
                  <Select value={form.auto_bid_style} onValueChange={(v) => setForm({ ...form, auto_bid_style: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="autobid-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dynamic">Dynamic — undercut competition, react to demand (recommended)</SelectItem>
                      <SelectItem value="conservative">Conservative — anchor near your historical accepted price</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    {form.auto_bid_style === "dynamic"
                      ? "AI aggressively undercuts the lowest competing bid, capped by your min-floor."
                      : "AI holds prices steady near your historical accepted average — safer margins."}
                  </p>
                </div>
                <div>
                  <Label className="hw-label">Hotel-wide min floor (₹/room/night)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.auto_bid_min_price || ""}
                    onChange={(e) => setForm({ ...form, auto_bid_min_price: e.target.value })}
                    placeholder="e.g. 1500"
                    className="rounded-sm mt-1"
                    data-testid="autobid-min-price"
                  />
                  <p className="text-xs text-slate-500 mt-1">AI will never quote below this. Room-level min prices also apply.</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="hw-label">Max discount % (from listed room price)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={80}
                    value={form.auto_bid_max_discount_pct || 25}
                    onChange={(e) => setForm({ ...form, auto_bid_max_discount_pct: Number(e.target.value) })}
                    className="rounded-sm mt-1 max-w-xs"
                    data-testid="autobid-max-discount"
                  />
                  <p className="text-xs text-slate-500 mt-1">AI will never quote below <b>listed price × (1 − {form.auto_bid_max_discount_pct || 25}%)</b>. Default 25%.</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5 flex flex-wrap gap-3 items-center">
                <Button
                  type="button"
                  onClick={save}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold py-5 h-auto px-7"
                  data-testid="autobid-save-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Save Auto-Bidding settings
                </Button>
                <a href="/partner/ai-drafts" className="text-blue-700 font-bold hover:underline text-sm" data-testid="autobid-view-drafts-link">
                  View AI drafts →
                </a>
              </div>

              <div className="mt-3 p-4 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600">
                <b className="text-slate-900">How it works</b>
                <ol className="list-decimal list-inside mt-1 space-y-1">
                  <li>Traveller posts a booking request in your city.</li>
                  <li>AI reads it, checks your past accepted prices, room min-prices, and any competing bids.</li>
                  <li>A draft bid appears in <b>AI drafts</b> — review, edit price/notes, or discard.</li>
                  <li>Hit <b>Send</b> and the bid goes to the traveller. You still control everything.</li>
                  <li>If the traveller counters, AI drafts a smart counter-reply too.</li>
                </ol>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="rules">
            <div className="hw-card p-7 mt-5 space-y-5" data-testid="rules-form">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-amber-500 to-red-500 grid place-items-center flex-shrink-0">
                  <ScrollText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="hw-label text-amber-700">House Rules & Regulations</div>
                  <h2 className="hw-head text-2xl md:text-3xl font-black text-slate-900 mt-1">Set your hotel policies</h2>
                  <p className="text-slate-600 mt-2 text-sm">These rules are shown to travellers on your listing and at booking. Tick the ones that apply and add any custom rule your hotel enforces.</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="hw-label">Check-in time</Label>
                  <Input type="time" value={form.checkin_time || "13:00"} onChange={(e) => setForm({ ...form, checkin_time: e.target.value })} className="rounded-sm mt-1" data-testid="rules-checkin-time" />
                </div>
                <div>
                  <Label className="hw-label">Check-out time</Label>
                  <Input type="time" value={form.checkout_time || "11:00"} onChange={(e) => setForm({ ...form, checkout_time: e.target.value })} className="rounded-sm mt-1" data-testid="rules-checkout-time" />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5">
                <Label className="hw-head font-bold text-slate-900 text-base">Preset rules</Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">Tick every rule your hotel enforces.</p>
                <div className="grid sm:grid-cols-2 gap-2" data-testid="rule-presets">
                  {rulePresets.map((r) => {
                    const checked = (form.rules_preset || []).includes(r.key);
                    return (
                      <label
                        key={r.key}
                        className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                          checked ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`rule-preset-${r.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRulePreset(r.key)}
                          className="w-4 h-4 mt-0.5 accent-emerald-600"
                        />
                        <span className="text-sm text-slate-800">{r.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5">
                <Label className="hw-head font-bold text-slate-900 text-base">Custom rules</Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">Add any additional house rules unique to your property.</p>
                <div className="flex gap-2">
                  <Input
                    value={customRule}
                    onChange={(e) => setCustomRule(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomRule())}
                    placeholder="e.g. Late check-in after 10pm requires prior intimation"
                    className="rounded-sm"
                    data-testid="custom-rule-input"
                  />
                  <Button type="button" onClick={addCustomRule} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="add-custom-rule-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                {(form.rules_custom || []).length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="custom-rules-list">
                    {(form.rules_custom || []).map((r, i) => (
                      <li key={`${r}-${i}`} className="flex items-start justify-between gap-2 p-2 border border-slate-200 rounded-sm bg-white text-sm">
                        <span className="flex-1 text-slate-800">• {r}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomRule(i)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`custom-rule-remove-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border-t border-slate-100 pt-5">
                <Button
                  type="button"
                  onClick={save}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold py-5 h-auto px-7"
                  data-testid="rules-save-btn"
                >
                  Save house rules
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="virtual">
            <VirtualRoomTab hotel={hotel} onUpdate={(vr) => setHotel({ ...hotel, virtual_room: vr })} />
          </TabsContent>

          <TabsContent value="pms">
            <PMSIntegrationTab />
          </TabsContent>

          <TabsContent value="staff">
            <StaffTab />
          </TabsContent>

          <TabsContent value="bank">
            <BankSettlementTab />
          </TabsContent>

          <TabsContent value="account">
            <form onSubmit={changePassword} className="hw-card p-7 mt-5 space-y-4 max-w-md" data-testid="password-form">
              <div className="hw-label text-blue-700">Change password</div>
              <h3 className="hw-head text-2xl font-black text-slate-900">Update your sign-in password</h3>
              <div><Label className="hw-label">Current password</Label><Input data-testid="pw-current" type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="hw-label">New password</Label><Input data-testid="pw-new" type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="hw-label">Confirm new password</Label><Input data-testid="pw-confirm" type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="rounded-sm mt-1" /></div>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-5 h-auto px-7" data-testid="pw-submit">
                <KeyRound className="w-4 h-4 mr-2" /> Change password
              </Button>
              <p className="text-xs text-slate-500">Google-authenticated users can&apos;t change a password here.</p>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


// ---------- Iter31: Virtual Room Tab (AI Virtual Room Experience™) ----------
function VirtualRoomTab({ hotel, onUpdate }) {
  const [urls, setUrls] = useState([]);
  const [inputUrl, setInputUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(hotel?.virtual_room || null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setResult(hotel?.virtual_room || null);
    // Pre-populate with existing panorama's source photos so the partner can
    // re-generate without re-uploading everything.
    if (hotel?.virtual_room?.source_photos) setUrls(hotel.virtual_room.source_photos);
  }, [hotel]);

  const addUrl = () => {
    const u = inputUrl.trim();
    if (!u) return;
    if (urls.length >= 12) return toast.error("You can add up to 12 photos");
    setUrls([...urls, u]);
    setInputUrl("");
  };
  const removeUrl = (i) => setUrls(urls.filter((_, x) => x !== i));

  const generate = async () => {
    if (urls.length < 4) return toast.error("Add at least 4 photos");
    setGenerating(true);
    try {
      const { data } = await api.post("/my/hotel/virtual-room/generate", { photo_urls: urls });
      setResult(data);
      onUpdate?.(data);
      if (data.status === "panorama") toast.success("AI stitched your panorama!");
      else toast.success("Guided tour ready — try more overlapping photos to unlock the panorama.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete the current virtual room?")) return;
    try {
      await api.delete("/my/hotel/virtual-room");
      setResult(null);
      onUpdate?.(null);
      toast.success("Virtual room removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="hw-card p-7 mt-5 space-y-6" data-testid="virtual-room-form">
      <div>
        <div className="hw-label text-emerald-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Virtual Room Experience™</div>
        <h3 className="hw-head text-2xl font-black text-slate-900 mt-1">Give travellers a 360° walk-through</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Upload <b>4-12 photos</b> of the same room (8+ recommended for a smoother stitch). Stand in the centre and take one every 45° (front, front-right, right, back-right, back, back-left, left, front-left).
          Our AI stitches them into a panoramic tour. If the photos don&apos;t overlap enough for a stitch, we automatically fall back to a swipeable guided tour.
        </p>
      </div>

      <div>
        <Label className="hw-label">Add photos (image URLs)</Label>
        <div className="flex gap-2 mt-2">
          <Input
            data-testid="vr-photo-url"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
            placeholder="https://image-url.jpg"
            className="rounded-sm"
          />
          <Button type="button" onClick={addUrl} disabled={urls.length >= 12} className="rounded-sm bg-slate-900 text-white" data-testid="vr-photo-add"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="text-xs text-slate-500 mt-1">Tip: paste public image links or use existing hotel photos. Currently {urls.length}/12.</div>

        <div className="grid grid-cols-4 md:grid-cols-6 gap-3 mt-4" data-testid="vr-photo-grid">
          {urls.map((u, i) => (
            <div key={i} className="relative aspect-square" data-testid={`vr-photo-${i}`}>
              <img src={u} alt="" className="w-full h-full object-cover rounded-sm border border-slate-200" />
              <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">{i + 1}</div>
              <button
                type="button"
                onClick={() => removeUrl(i)}
                data-testid={`vr-photo-remove-${i}`}
                className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 grid place-items-center text-xs rounded-full"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Button
          onClick={generate}
          disabled={generating || urls.length < 4}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold h-11 px-5"
          data-testid="vr-generate"
        >
          <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Stitching with AI…" : (result ? "Regenerate" : "Generate Virtual Room")}
        </Button>
        {result && (
          <Button variant="outline" onClick={remove} className="rounded-sm border-red-300 text-red-700" data-testid="vr-delete">
            Remove
          </Button>
        )}
      </div>

      {result && (
        <div className={`p-5 rounded-sm border-2 ${result.status === "panorama" ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="vr-result">
          <div className="hw-head font-black text-lg text-slate-900 capitalize" data-testid="vr-result-status">
            {result.status === "panorama" ? "Panorama ready" : "Guided tour ready"}
          </div>
          {result.reason && <p className="text-xs text-slate-700 mt-1">{result.reason}</p>}
          {result.status === "panorama" && hotel?.hotel_id && (
            <div className="mt-3 aspect-[3/1] bg-black overflow-hidden rounded-sm">
              <img
                src={`${API}/api/public/hotels/${hotel.slug || hotel.hotel_id}/panorama`}
                alt="Panorama preview"
                className="w-full h-full object-cover"
                data-testid="vr-preview"
              />
            </div>
          )}
          <div className="text-xs text-slate-500 mt-2">
            Generated {result.generated_at?.slice(0, 16).replace("T", " ")} · {result.source_photos?.length || urls.length} source photos
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Iter46: Full PMS Integration Hub ----------
function PMSIntegrationTab() {
  const [vendors, setVendors] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("generic");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [creds, setCreds] = useState({ api_key: "", api_secret: "", hotel_code: "", property_id: "", username: "", password: "" });
  const [secretJustIssued, setSecretJustIssued] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [mapping, setMapping] = useState([]); // [{vendor_code, room_id}]

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/pms/vendors").then(({ data }) => setVendors(data.vendors || [])),
      api.get("/my/hotel/channel").then(({ data }) => {
        setStatus(data);
        if (data.provider) setProvider(data.provider);
        if (data.callback_url) setCallbackUrl(data.callback_url);
        const m = data.room_mapping || {};
        setMapping(Object.entries(m).map(([vendor_code, room_id]) => ({ vendor_code, room_id })));
      }),
      api.get("/my/rooms").then(({ data }) => setRooms(Array.isArray(data) ? data : (data.rooms || []))).catch(() => setRooms([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/my/hotel/channel/connect", {
        provider,
        callback_url: callbackUrl || undefined,
        credentials: creds,
      });
      setSecretJustIssued(data.channel_secret);
      toast.success("Connected. Copy your channel secret below.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    } finally { setConnecting(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/my/hotel/channel/test-connection");
      if (data.ok) toast.success(`${data.message} (${data.latency_ms}ms)`);
      else toast.error(data.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const importProperty = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/my/hotel/channel/import");
      setImportResult(data);
      toast.success(`Imported ${data.rooms?.length || 0} rooms + ${data.rates?.length || 0} rate plans.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/my/hotel/channel/sync-now");
      toast.success(`Synced ${data.entries_count} entries for next ${data.days} days.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const saveMapping = async () => {
    const m = {};
    mapping.forEach(({ vendor_code, room_id }) => {
      if (vendor_code && room_id) m[vendor_code.trim()] = room_id.trim();
    });
    try {
      await api.put("/my/hotel/channel/room-mapping", { mapping: m });
      toast.success("Room mapping saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect PMS? SwaanStay will stop syncing with your PMS.")) return;
    try {
      await api.post("/my/hotel/channel/disconnect");
      setSecretJustIssued(null);
      setImportResult(null);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text || ""); toast.success("Copied"); };

  if (loading) return <div className="hw-card p-10 text-center text-slate-500">Loading PMS Hub...</div>;

  const isConnected = status?.pms_connected;
  const health = status?.health || "red";
  const healthDot = { green: "bg-emerald-500 animate-pulse", amber: "bg-amber-500", red: "bg-red-500" }[health];
  const healthLabel = { green: "🟢 Connected", amber: "🟡 Syncing / Stale", red: "🔴 Disconnected" }[health];
  const selectedVendor = vendors.find((v) => v.vendor === provider);

  return (
    <div className="space-y-5 mt-5" data-testid="pms-tab">
      {/* Intro */}
      <div className="hw-card p-7">
        <div className="hw-label text-emerald-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> PMS Integration Hub</div>
        <h3 className="hw-head text-2xl font-black text-slate-900 mt-1">Connect your PMS in 3 steps</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Pick your PMS → paste your credentials → import property. SwaanStay will keep inventory + prices in sync,
          push confirmed bookings back to your system, and free the room instantly on every cancellation.
          <br /><span className="text-xs text-slate-500">Set per-room AI negotiation rules below to control how our AI bargains with travellers.</span>
        </p>
      </div>

      {/* Health card */}
      <div className={`hw-card p-6 border-l-4 ${health === "green" ? "border-l-emerald-500" : health === "amber" ? "border-l-amber-500" : "border-l-red-400"}`} data-testid="pms-health">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="hw-label text-slate-500">Connection health</div>
            <div className="hw-head font-black text-xl mt-1 text-slate-900 flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${healthDot}`} /> {healthLabel}
              {isConnected && <span className="text-slate-500 font-normal text-sm">· {selectedVendor?.label || status.provider}</span>}
            </div>
            {isConnected && (
              <div className="text-xs text-slate-500 mt-1 space-x-4">
                <span>channel_id <code className="bg-slate-100 px-1 rounded">{status.channel_id}</code></span>
                {status.last_sync_at && <span>last sync {status.last_sync_at.slice(0, 16).replace("T", " ")}</span>}
                {status.last_sync_rows !== null && status.last_sync_rows !== undefined && <span>{status.last_sync_rows} rows</span>}
                {status.dead_letters > 0 && <span className="text-red-600 font-bold">{status.dead_letters} failed webhooks</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {isConnected && (
              <>
                <Button onClick={testConnection} disabled={testing} variant="outline" className="rounded-sm" data-testid="pms-test">
                  {testing ? "Testing…" : "Test Connection"}
                </Button>
                <Button onClick={syncNow} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="pms-sync-now">
                  {syncing ? "Syncing…" : "Sync Now"}
                </Button>
                <Button onClick={disconnect} variant="outline" className="rounded-sm border-red-300 text-red-700" data-testid="pms-disconnect">Disconnect</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Vendor picker */}
      <div className="hw-card p-6 space-y-4" data-testid="pms-connect-form">
        <div className="hw-head text-lg font-black text-slate-900">Step 1 — Which PMS do you use?</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="pms-vendor-picker">
          {vendors.map((v) => (
            <button
              key={v.vendor}
              type="button"
              onClick={() => setProvider(v.vendor)}
              data-testid={`pms-vendor-${v.vendor}`}
              className={`p-3 border rounded-sm text-left transition-all relative group ${provider === v.vendor ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-emerald-400"}`}
            >
              <div className="font-bold text-sm text-slate-900">{v.label}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <span>{v.kind}</span>
                <span>·</span>
                <span>{v.region}</span>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/pms/vendors/${v.vendor}/guide`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="absolute top-1 right-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity underline"
                data-testid={`pms-guide-${v.vendor}`}
              >
                Guide PDF ↗
              </a>
            </button>
          ))}
        </div>

        {/* Credentials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100" data-testid="pms-credentials">
          <div className="md:col-span-2">
            <div className="hw-head text-lg font-black text-slate-900">Step 2 — Paste credentials from {selectedVendor?.label || "your PMS"}</div>
            <p className="text-xs text-slate-500 mt-1">Get these from your {selectedVendor?.label} admin console → API / Integrations section.</p>
          </div>
          <div>
            <Label className="hw-label">API Key</Label>
            <Input value={creds.api_key} onChange={(e) => setCreds({ ...creds, api_key: e.target.value })} placeholder="apk_xxx" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-api-key" />
          </div>
          <div>
            <Label className="hw-label">API Secret</Label>
            <Input type="password" value={creds.api_secret} onChange={(e) => setCreds({ ...creds, api_secret: e.target.value })} placeholder="•••••••••" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-api-secret" />
          </div>
          <div>
            <Label className="hw-label">Hotel Code</Label>
            <Input value={creds.hotel_code} onChange={(e) => setCreds({ ...creds, hotel_code: e.target.value })} placeholder="H-1023" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-hotel-code" />
          </div>
          <div>
            <Label className="hw-label">Property ID (if separate)</Label>
            <Input value={creds.property_id} onChange={(e) => setCreds({ ...creds, property_id: e.target.value })} placeholder="P-4577" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-property-id" />
          </div>
          <div className="md:col-span-2 text-xs text-slate-500 italic">
            Or use Username/Password auth (some PMS/CM&apos;s use this):
          </div>
          <div>
            <Label className="hw-label">Username</Label>
            <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="hotel_admin" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-username" />
          </div>
          <div>
            <Label className="hw-label">Password</Label>
            <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="•••••••••" className="rounded-sm mt-1 font-mono" data-testid="pms-cred-password" />
          </div>
        </div>

        <div>
          <Label className="hw-label">Outbound Webhook URL (optional)</Label>
          <Input
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            placeholder="https://your-pms.example.com/hooks/swaanstay"
            className="rounded-sm mt-1"
            data-testid="pms-callback-url"
          />
          <p className="text-xs text-slate-500 mt-1">SwaanStay will POST signed booking events (<code>booking.confirmed</code>, <code>booking.cancelled</code>, <code>refund.processed</code>) here.</p>
        </div>

        <div className="flex gap-2">
          <Button onClick={connect} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="pms-connect">
            {connecting ? "Connecting…" : (isConnected ? "Reconnect / Rotate secret" : "Connect")}
          </Button>
          {isConnected && (
            <Button onClick={importProperty} disabled={importing} variant="outline" className="rounded-sm font-bold" data-testid="pms-import">
              {importing ? "Importing…" : "Import property (preview)"}
            </Button>
          )}
        </div>
      </div>

      {/* Freshly issued secret */}
      {secretJustIssued && (
        <div className="hw-card p-6 border-2 border-emerald-500 bg-emerald-50" data-testid="pms-secret-issued">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <div className="hw-head font-black text-slate-900">Save these credentials now — the secret is only shown once</div>
          </div>
          <div className="mt-4 space-y-2">
            <div>
              <div className="hw-label text-slate-500">channel_id</div>
              <div className="flex items-center gap-2 mt-1">
                <code className="bg-white border border-slate-200 px-3 py-2 rounded-sm text-sm flex-1 font-mono" data-testid="pms-channel-id">{status?.channel_id}</code>
                <Button type="button" size="sm" variant="outline" onClick={() => copy(status?.channel_id)} className="rounded-sm">Copy</Button>
              </div>
            </div>
            <div>
              <div className="hw-label text-slate-500">channel_secret</div>
              <div className="flex items-center gap-2 mt-1">
                <code className="bg-white border border-slate-200 px-3 py-2 rounded-sm text-sm flex-1 font-mono break-all" data-testid="pms-channel-secret">{secretJustIssued}</code>
                <Button type="button" size="sm" variant="outline" onClick={() => copy(secretJustIssued)} className="rounded-sm">Copy</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import preview */}
      {importResult && (
        <div className="hw-card p-6" data-testid="pms-import-preview">
          <div className="hw-head text-lg font-black text-slate-900">Step 3 — Review imported property</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
            <div className="bg-slate-50 p-3 rounded-sm"><div className="hw-label text-slate-500">Rooms</div><div className="text-2xl font-black">{importResult.rooms?.length || 0}</div></div>
            <div className="bg-slate-50 p-3 rounded-sm"><div className="hw-label text-slate-500">Rate plans</div><div className="text-2xl font-black">{importResult.rates?.length || 0}</div></div>
            <div className="bg-slate-50 p-3 rounded-sm"><div className="hw-label text-slate-500">Amenities</div><div className="text-2xl font-black">{importResult.amenities?.length || 0}</div></div>
            <div className="bg-slate-50 p-3 rounded-sm"><div className="hw-label text-slate-500">Meal plans</div><div className="text-2xl font-black">{importResult.meal_plans?.length || 0}</div></div>
          </div>
          {importResult.rooms?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500 border-b">
                  <tr><th className="py-2">Room</th><th>Beds</th><th>Occupancy</th><th>Units</th><th>Base ₹</th></tr>
                </thead>
                <tbody>
                  {importResult.rooms.map((r) => (
                    <tr key={r.room_id} className="border-b border-slate-100">
                      <td className="py-2 font-bold">{r.room_name} <span className="text-slate-400 font-normal">({r.room_id})</span></td>
                      <td>{r.beds}</td>
                      <td>{r.occupancy}</td>
                      <td>{r.total_units}</td>
                      <td>₹{Number(r.base_price).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Room mapping table */}
      {isConnected && (
        <div className="hw-card p-6" data-testid="pms-room-mapping">
          <div className="hw-head text-lg font-black text-slate-900">Room mapping</div>
          <p className="text-xs text-slate-500 mt-1">Translate your PMS&apos;s room codes → SwaanStay room IDs so inventory syncs correctly.</p>
          <div className="mt-3 space-y-2">
            {mapping.map((row, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Input placeholder="Vendor code (e.g. RT_101)" value={row.vendor_code} onChange={(e) => { const m = [...mapping]; m[i].vendor_code = e.target.value; setMapping(m); }} className="rounded-sm font-mono" data-testid={`map-vendor-${i}`} />
                <div className="flex gap-2">
                  <select value={row.room_id} onChange={(e) => { const m = [...mapping]; m[i].room_id = e.target.value; setMapping(m); }} className="flex-1 border border-slate-200 rounded-sm px-2 text-sm" data-testid={`map-room-${i}`}>
                    <option value="">— Select SwaanStay room —</option>
                    {rooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.room_name} ({r.room_id})</option>)}
                  </select>
                  <Button type="button" size="sm" variant="outline" onClick={() => setMapping(mapping.filter((_, j) => j !== i))} className="rounded-sm text-red-600">×</Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setMapping([...mapping, { vendor_code: "", room_id: "" }])} className="rounded-sm" data-testid="map-add-row">+ Add row</Button>
          </div>
          <div className="mt-3">
            <Button onClick={saveMapping} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="map-save">Save mapping</Button>
          </div>
        </div>
      )}

      {/* Per-room AI rules */}
      <div className="hw-card p-6" data-testid="pms-ai-rules">
        <div className="hw-head text-lg font-black text-slate-900">AI Negotiation Rules — per room</div>
        <p className="text-xs text-slate-500 mt-1">Set the guardrails our AI uses when travellers negotiate. Rules are stored on SwaanStay side (not in your PMS).</p>
        <div className="mt-4 space-y-3">
          {rooms.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Add rooms in the Rooms tab first.</div>
          ) : (
            rooms.map((r) => <RoomAIRulesRow key={r.room_id} room={r} />)
          )}
        </div>
      </div>

      {/* Integration docs (kept from before) */}
      <div className="hw-card p-6" data-testid="pms-docs">
        <div className="hw-head text-lg font-black text-slate-900">Advanced — API reference</div>
        <p className="text-sm text-slate-600 mt-2">Universal REST API. Sign every request with HMAC-SHA256 of <code>timestamp.body</code>.</p>
        <div className="mt-4 space-y-3">
          <div className="bg-slate-900 text-slate-100 p-4 rounded-sm overflow-x-auto text-xs font-mono">
            <div className="text-emerald-400 font-bold mb-1"># Push availability + base price</div>
            <div>POST /api/channel/{provider}/availability</div>
            <div>Headers: X-SwaanStay-Signature, X-SwaanStay-Timestamp, X-SwaanStay-Channel-Id</div>
          </div>
          <div className="bg-slate-900 text-slate-100 p-4 rounded-sm overflow-x-auto text-xs font-mono">
            <div className="text-emerald-400 font-bold mb-1"># Outbound webhook (SwaanStay → your PMS)</div>
            <div>{`POST ${callbackUrl || "<your webhook url>"}`}</div>
            <div>{`event ∈ "booking.confirmed" | "booking.cancelled" | "refund.processed"`}</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function RoomAIRulesRow({ room }) {
  const [expanded, setExpanded] = useState(false);
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/my/rooms/${room.room_id}/ai-rules`).then(({ data }) => setRules(data));
  };
  useEffect(() => { if (expanded && !rules) load(); }, [expanded, rules]); // load once when expanded

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/my/rooms/${room.room_id}/ai-rules`, rules);
      setRules(data);
      toast.success(`AI rules saved for ${room.room_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 rounded-sm" data-testid={`ai-rules-${room.room_id}`}>
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full p-3 text-left hover:bg-slate-50 flex items-center justify-between" data-testid={`ai-rules-toggle-${room.room_id}`}>
        <div>
          <div className="font-bold text-sm text-slate-900">{room.room_name}</div>
          <div className="text-xs text-slate-500">Base ₹{Number(room.base_price || 0).toLocaleString("en-IN")} · {room.total_units || 0} units</div>
        </div>
        <div className="text-emerald-600 font-bold text-sm">{expanded ? "Hide" : "Configure ▸"}</div>
      </button>
      {expanded && rules && (
        <div className="p-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="hw-label">Max negotiation %</Label>
            <Input type="number" min="0" max="60" value={rules.max_negotiation_pct ?? 10} onChange={(e) => setRules({ ...rules, max_negotiation_pct: Number(e.target.value) })} className="rounded-sm mt-1" data-testid={`ai-max-neg-${room.room_id}`} />
          </div>
          <div>
            <Label className="hw-label">Minimum selling price (₹)</Label>
            <Input type="number" min="0" max={room.base_price} value={rules.min_selling_price ?? ""} onChange={(e) => setRules({ ...rules, min_selling_price: e.target.value === "" ? null : Number(e.target.value) })} placeholder="Auto" className="rounded-sm mt-1" data-testid={`ai-min-price-${room.room_id}`} />
          </div>
          <div>
            <Label className="hw-label">Corporate discount %</Label>
            <Input type="number" min="0" max="60" value={rules.corporate_discount_pct ?? 15} onChange={(e) => setRules({ ...rules, corporate_discount_pct: Number(e.target.value) })} className="rounded-sm mt-1" data-testid={`ai-corp-${room.room_id}`} />
          </div>
          <div>
            <Label className="hw-label">Group discount %</Label>
            <Input type="number" min="0" max="60" value={rules.group_discount_pct ?? 20} onChange={(e) => setRules({ ...rules, group_discount_pct: Number(e.target.value) })} className="rounded-sm mt-1" data-testid={`ai-group-${room.room_id}`} />
          </div>
          <div>
            <Label className="hw-label">Weekend minimum discount %</Label>
            <Input type="number" min="0" max="30" value={rules.weekend_min_discount_pct ?? 3} onChange={(e) => setRules({ ...rules, weekend_min_discount_pct: Number(e.target.value) })} className="rounded-sm mt-1" data-testid={`ai-weekend-${room.room_id}`} />
          </div>
          <div className="flex items-center gap-4 pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!rules.ai_negotiation_enabled} onChange={(e) => setRules({ ...rules, ai_negotiation_enabled: e.target.checked })} className="w-4 h-4" data-testid={`ai-enabled-${room.room_id}`} />
              <span className="text-sm font-bold">AI Negotiation ON</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!rules.festival_negotiation_enabled} onChange={(e) => setRules({ ...rules, festival_negotiation_enabled: e.target.checked })} className="w-4 h-4" data-testid={`ai-festival-${room.room_id}`} />
              <span className="text-sm font-bold">Festival negotiation</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={`ai-save-${room.room_id}`}>
              {saving ? "Saving…" : "Save rules"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



// ---------- Iter48: Staff RBAC tab ----------
function StaffTab() {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [inv, setInv] = useState({ name: "", phone: "", email: "", staff_role: "reception", scoped_hotels: [], scoped_region: "" });
  const [saving, setSaving] = useState(false);

  const isOwner = user?.role === "hotel_partner";

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/my/staff").then(({ data }) => { setStaffList(data.staff || []); setInvitations(data.invitations || []); }).catch(() => {}),
      api.get("/my/properties").then(({ data }) => setProperties(Array.isArray(data) ? data : [])).catch(() => setProperties([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!isOwner) {
    return <div className="hw-card p-8 mt-5 text-center text-slate-500 italic" data-testid="staff-owner-only">Only the chain owner can manage staff.</div>;
  }
  if (loading) return <div className="hw-card p-10 text-center text-slate-500 mt-5">Loading staff…</div>;

  const roleNeedsHotels = inv.staff_role !== "regional_mgr";
  const roleNeedsRegion = inv.staff_role === "regional_mgr";

  const toggleHotel = (hid) => {
    setInv((prev) => ({
      ...prev,
      scoped_hotels: prev.scoped_hotels.includes(hid)
        ? prev.scoped_hotels.filter((h) => h !== hid)
        : [...prev.scoped_hotels, hid],
    }));
  };

  const send = async () => {
    if (!inv.name.trim() || !inv.phone.trim()) return toast.error("Name + phone are required");
    if (roleNeedsHotels && inv.scoped_hotels.length === 0) return toast.error("Pick at least one property");
    if (roleNeedsRegion && !inv.scoped_region.trim()) return toast.error("Region label is required");
    setSaving(true);
    try {
      const { data } = await api.post("/my/staff/invite", inv);
      toast.success("WhatsApp invitation sent");
      if (data.accept_link) {
        console.info("Magic link:", data.accept_link);
      }
      setOpenInvite(false);
      setInv({ name: "", phone: "", email: "", staff_role: "reception", scoped_hotels: [], scoped_region: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setSaving(false); }
  };

  const revoke = async (staff_id, name) => {
    if (!window.confirm(`Revoke ${name}'s access? They will be signed out immediately.`)) return;
    try {
      await api.delete(`/my/staff/${staff_id}`);
      toast.success("Access revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  const roleBadge = (r) => (
    <span className={`hw-chip text-[10px] ${{
      regional_mgr: "border-purple-400 bg-purple-50 text-purple-800",
      property_mgr: "border-blue-400 bg-blue-50 text-blue-800",
      reception: "border-emerald-400 bg-emerald-50 text-emerald-800",
      finance: "border-amber-400 bg-amber-50 text-amber-800",
    }[r] || "border-slate-300 bg-slate-50 text-slate-700"}`}>
      {r.replace("_", " ")}
    </span>
  );

  return (
    <div className="space-y-5 mt-5" data-testid="staff-tab">
      <div className="hw-card p-6 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="hw-label text-emerald-700">Staff & permissions</div>
          <h3 className="hw-head text-xl font-black text-slate-900 mt-1">Invite team members</h3>
          <p className="text-sm text-slate-600 mt-1">Roles: Regional Manager · Property Manager · Reception · Finance. Reception can only see check-in details unless you toggle full-view per property.</p>
        </div>
        <Button onClick={() => setOpenInvite(!openInvite)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="staff-invite-btn">Invite staff</Button>
      </div>

      {openInvite && (
        <div className="hw-card p-6" data-testid="staff-invite-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="hw-label">Full name *</Label>
              <Input value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="rounded-sm mt-1" data-testid="staff-name" />
            </div>
            <div>
              <Label className="hw-label">WhatsApp phone (with country code) *</Label>
              <Input value={inv.phone} onChange={(e) => setInv({ ...inv, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="rounded-sm mt-1" data-testid="staff-phone" />
            </div>
            <div>
              <Label className="hw-label">Email (optional)</Label>
              <Input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} placeholder="staff@example.com" className="rounded-sm mt-1" data-testid="staff-email" />
            </div>
            <div>
              <Label className="hw-label">Role *</Label>
              <select value={inv.staff_role} onChange={(e) => setInv({ ...inv, staff_role: e.target.value, scoped_hotels: [], scoped_region: "" })} className="w-full h-10 border border-slate-200 rounded-sm mt-1 px-3 text-sm" data-testid="staff-role">
                <option value="regional_mgr">Regional Manager</option>
                <option value="property_mgr">Property Manager</option>
                <option value="reception">Reception</option>
                <option value="finance">Finance</option>
              </select>
            </div>
            {roleNeedsRegion && (
              <div className="md:col-span-2">
                <Label className="hw-label">Region label *</Label>
                <Input value={inv.scoped_region} onChange={(e) => setInv({ ...inv, scoped_region: e.target.value })} placeholder="e.g. South India, Metros" className="rounded-sm mt-1" data-testid="staff-region" />
                <p className="text-xs text-slate-500 mt-1">Tag your properties with this region label (Hotel Details tab) so the RM sees them.</p>
              </div>
            )}
            {roleNeedsHotels && (
              <div className="md:col-span-2">
                <Label className="hw-label">Properties this staff can access *</Label>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-sm p-2" data-testid="staff-hotels-picker">
                  {properties.map((p) => (
                    <label key={p.hotel_id} className="flex items-center gap-2 p-1 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={inv.scoped_hotels.includes(p.hotel_id)} onChange={() => toggleHotel(p.hotel_id)} className="w-4 h-4" data-testid={`staff-hotel-${p.hotel_id}`} />
                      <span className="text-sm">{p.name} <span className="text-slate-500">· {p.city}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={send} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="staff-send">
              {saving ? "Sending…" : "Send WhatsApp invitation"}
            </Button>
            <Button variant="outline" onClick={() => setOpenInvite(false)} className="rounded-sm">Cancel</Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Staff opens the WhatsApp link, sets their password, and lands in the Partner Portal with role-scoped access.</p>
        </div>
      )}

      {invitations.length > 0 && (
        <div className="hw-card p-6" data-testid="staff-invitations">
          <div className="hw-head text-lg font-black text-slate-900">Pending invitations</div>
          <div className="mt-3 space-y-2">
            {invitations.map((i) => (
              <div key={i.invitation_id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-sm">
                <div>
                  <div className="font-bold text-slate-900">{i.staff_name} · <span className="text-slate-500 font-normal">{i.staff_phone}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">{roleBadge(i.staff_role)} <span>invited {i.created_at?.slice(0, 16).replace("T", " ")}</span></div>
                </div>
                <span className="hw-chip border-amber-400 bg-white text-amber-800">Awaiting activation</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="hw-card p-6" data-testid="staff-list">
        <div className="hw-head text-lg font-black text-slate-900">Active staff ({staffList.length})</div>
        {staffList.length === 0 ? (
          <div className="text-sm text-slate-500 italic mt-2">No staff yet. Invite your first team member above.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {staffList.map((s) => (
              <div key={s.user_id} className="flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:bg-slate-50" data-testid={`staff-row-${s.user_id}`}>
                <div>
                  <div className="font-bold text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    {roleBadge(s.staff_role || "reception")}
                    <span>{s.email}</span>
                    <span>·</span>
                    <span>{s.phone}</span>
                    {s.scoped_region && <span>· region: <b>{s.scoped_region}</b></span>}
                    {s.scoped_hotels?.length > 0 && <span>· {s.scoped_hotels.length} propert{s.scoped_hotels.length === 1 ? "y" : "ies"}</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => revoke(s.user_id, s.name)} className="rounded-sm border-red-300 text-red-700" data-testid={`revoke-${s.user_id}`}>Revoke</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Iter49: Bank & Settlement (Razorpay Route) ----------
function BankSettlementTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [chain, setChain] = useState(null);
  const [form, setForm] = useState({ beneficiary_name: "", account_number: "", ifsc: "", account_type: "current", contact_name: "", contact_phone: "", contact_email: "", pan: "", gst: "", legal_business_name: "", business_type: "partnership", address: "", state: "TG", postal_code: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const isOwner = user?.role === "hotel_partner";

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/my/hotel/bank-account").then(({ data }) => {
        setData(data);
        if (data?.bank_account) setForm((f) => ({ ...f, ...data.bank_account }));
      }).catch(() => setData(null)),
      api.get("/my/chain").then(({ data }) => setChain(data)).catch(() => setChain(null)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!isOwner) return <div className="hw-card p-8 mt-5 text-center text-slate-500 italic" data-testid="bank-owner-only">Only the chain owner can manage bank & settlement.</div>;
  if (loading) return <div className="hw-card p-10 text-center text-slate-500 mt-5">Loading…</div>;

  const save = async () => {
    if (!form.beneficiary_name || !form.account_number || !form.ifsc) return toast.error("Beneficiary, account number and IFSC are required.");
    setSaving(true);
    try {
      const { data: res } = await api.post("/my/hotel/bank-account", form);
      if (res.error) toast.warning(`Bank saved. Razorpay Route: ${res.error.slice(0, 120)}`);
      else toast.success("Bank details saved. Razorpay Route linked account created.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/my/hotel/linked-account/sync");
      toast.success(`Status: ${res.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const setSettlementMode = async (mode) => {
    try {
      await api.patch("/my/chain", { chain_settlement_mode: mode });
      toast.success(`Settlement mode: ${mode}`);
      const { data } = await api.get("/my/chain");
      setChain(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const status = data?.linked_account_status || "none";
  const statusColour = { none: "bg-slate-200 text-slate-700", created: "bg-amber-100 text-amber-800", under_review: "bg-amber-100 text-amber-800", needs_clarification: "bg-red-100 text-red-800", activated: "bg-emerald-100 text-emerald-800", active: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800" }[status] || "bg-slate-200 text-slate-700";
  const commissionPct = data?.commission_pct ?? 2;

  return (
    <div className="space-y-5 mt-5" data-testid="bank-tab">
      {/* Intro */}
      <div className="hw-card p-6">
        <div className="hw-label text-emerald-700">Bank & Settlement · Razorpay Route</div>
        <h3 className="hw-head text-xl font-black text-slate-900 mt-1">Get paid directly to your bank</h3>
        <p className="text-sm text-slate-600 mt-1">
          Every SwaanStay payment auto-splits at the gateway: <b>{(100 - commissionPct).toFixed(0)}%</b> settles directly to your bank; SwaanStay retains a flat <b>{commissionPct}%</b> only to cover Razorpay payment-gateway charges. Zero booking commission. Refunds reverse automatically.
        </p>
      </div>

      {/* Chain settlement mode */}
      {chain?.is_chain && (
        <div className="hw-card p-6" data-testid="settlement-mode">
          <div className="hw-head text-base font-black text-slate-900">Settlement mode (chain-wide)</div>
          <p className="text-xs text-slate-500 mt-1">Where should each property&apos;s revenue land?</p>
          <div className="flex gap-2 mt-3">
            {[{ k: "property", label: "Per-property account (recommended)" }, { k: "central", label: "Central account (all properties → one bank)" }].map((opt) => (
              <button key={opt.k} onClick={() => setSettlementMode(opt.k)} className={`flex-1 p-3 border rounded-sm text-left transition-all ${chain?.settlement_mode === opt.k ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-emerald-400"}`} data-testid={`settle-${opt.k}`}>
                <div className="font-bold text-sm text-slate-900">{opt.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Linked account status */}
      <div className="hw-card p-6" data-testid="linked-account-status">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="hw-label text-slate-500">Linked account status</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`hw-chip font-black ${statusColour}`} data-testid="linked-status">{status.replace(/_/g, " ").toUpperCase()}</span>
              {data?.linked_account_id && <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{data.linked_account_id}</code>}
            </div>
            {status === "created" || status === "under_review" ? (
              <p className="text-xs text-amber-700 mt-2">KYC under review with Razorpay — usually completes in 24-48 hrs. Meanwhile bookings still settle to SwaanStay&apos;s platform account and get paid out on Fridays.</p>
            ) : status === "active" || status === "activated" ? (
              <p className="text-xs text-emerald-700 mt-2">Route active — funds settle to your bank instantly on every booking.</p>
            ) : status === "none" ? (
              <p className="text-xs text-slate-500 mt-2">Save your bank details below to create a Razorpay Route linked account for direct settlement.</p>
            ) : (
              <p className="text-xs text-red-700 mt-2">Razorpay needs more info. Log in to your Razorpay Dashboard → Route → Linked Accounts to resolve.</p>
            )}
          </div>
          {data?.linked_account_id && (
            <Button onClick={sync} disabled={syncing} variant="outline" className="rounded-sm" data-testid="sync-linked">
              {syncing ? "Syncing…" : "Refresh status"}
            </Button>
          )}
        </div>
      </div>

      {/* Bank form */}
      <div className="hw-card p-6" data-testid="bank-form">
        <div className="hw-head text-lg font-black text-slate-900">Bank account details</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="hw-label">Beneficiary name (as per bank) *</Label>
            <Input value={form.beneficiary_name} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} className="rounded-sm mt-1" data-testid="bank-beneficiary" />
          </div>
          <div>
            <Label className="hw-label">Account number *</Label>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="bank-account-number" />
          </div>
          <div>
            <Label className="hw-label">IFSC *</Label>
            <Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className="rounded-sm mt-1 font-mono" data-testid="bank-ifsc" />
          </div>
          <div>
            <Label className="hw-label">Account type</Label>
            <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="w-full h-10 border border-slate-200 rounded-sm mt-1 px-3 text-sm" data-testid="bank-account-type">
              <option value="current">Current</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <div className="md:col-span-2 pt-3 border-t border-slate-100">
            <div className="hw-label text-slate-500">KYC details (needed once, for Razorpay Route)</div>
          </div>
          <div>
            <Label className="hw-label">Legal business name</Label>
            <Input value={form.legal_business_name} onChange={(e) => setForm({ ...form, legal_business_name: e.target.value })} className="rounded-sm mt-1" data-testid="bank-legal-name" />
          </div>
          <div>
            <Label className="hw-label">Business type</Label>
            <select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className="w-full h-10 border border-slate-200 rounded-sm mt-1 px-3 text-sm" data-testid="bank-business-type">
              <option value="proprietorship">Proprietorship</option>
              <option value="partnership">Partnership</option>
              <option value="private_limited">Private Limited</option>
              <option value="public_limited">Public Limited</option>
              <option value="llp">LLP</option>
              <option value="individual">Individual</option>
              <option value="trust">Trust</option>
            </select>
          </div>
          <div>
            <Label className="hw-label">PAN</Label>
            <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="bank-pan" />
          </div>
          <div>
            <Label className="hw-label">GSTIN (optional)</Label>
            <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="bank-gst" />
          </div>
          <div>
            <Label className="hw-label">Contact name</Label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm mt-1" data-testid="bank-contact-name" />
          </div>
          <div>
            <Label className="hw-label">Contact phone</Label>
            <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm mt-1" data-testid="bank-contact-phone" />
          </div>
          <div>
            <Label className="hw-label">Contact email</Label>
            <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-sm mt-1" data-testid="bank-contact-email" />
          </div>
          <div className="md:col-span-2">
            <Label className="hw-label">Registered address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1" data-testid="bank-address" />
          </div>
          <div>
            <Label className="hw-label">State code (e.g. TG, KA, MH)</Label>
            <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="bank-state" />
          </div>
          <div>
            <Label className="hw-label">Postal code</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="bank-postal" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="bank-save">
            {saving ? "Saving…" : "Save & create linked account"}
          </Button>
          <p className="text-xs text-slate-500 mt-2">
            Payment-gateway fee: <b>{commissionPct}%</b> (0% SwaanStay commission). On a ₹1,000 booking, you receive <b>₹{(1000 * (100 - commissionPct) / 100).toFixed(0)}</b> directly to your bank.
          </p>
        </div>
      </div>
    </div>
  );
}
